import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "hasCompetencesCheckbox",
    "evaluatedCategoriesSection",
    "evaluatedCategoriesList",
    "evaluatedCategoriesContainer",
    "evaluatedCategoryItem",
    "evaluatedCategoryName",
    "evaluatedCategoryScale",
    "evaluatedCategoryScaleField",
    "scaleInput",
    "scaleField",
    "coefficientField",
    "scaleSummary",
    "totalScaleDisplay",
    "subjectGroupMap",
    "subjectGroupHint"
  ]

  static values = {
    isNursery: Boolean
  }

  onSubjectChange() {
    this.refreshSubjectGroupHint()
  }

  subjectGroupMapTargetConnected() {
    this.refreshSubjectGroupHint()
  }

  subjectGroupHintTargetConnected() {
    this.refreshSubjectGroupHint()
  }

  // Tells which group the blank choice stands for: the chosen subject's own.
  refreshSubjectGroupHint() {
    if (!this.hasSubjectGroupMapTarget || !this.hasSubjectGroupHintTarget) return

    const groups = JSON.parse(this.subjectGroupMapTarget.textContent || "{}")
    const subjectId = this.element.querySelector("#course_subject_id")?.value
    const group = groups[subjectId]
    const hint = this.subjectGroupHintTarget
    hint.textContent = group ? hint.dataset.template.replace("%{group}", group) : ""
    hint.hidden = !group
  }

  connect() {
    console.log("Course form controller connected")
    console.log("Is nursery:", this.isNurseryValue)
    
    // Initialize teaching cycle tracking
    this.currentTeachingCycle = null
    this.pendingSubjectSelection = null
    
    // Check if we're editing and have existing data
    const classroomSelect = document.getElementById("course_classroom_id")
    const subjectSelect = document.getElementById("course_subject_id")
    
    if (classroomSelect && subjectSelect) {
      const existingClassroomId = subjectSelect.dataset.existingClassroomId
      const existingSubjectId = subjectSelect.dataset.existingSubjectId
      
      if (existingClassroomId && existingSubjectId) {
        // Store the existing subject ID for later selection
        this.pendingSubjectSelection = existingSubjectId
        
        // We're editing, load teaching cycle first, then subjects
        this.loadClassroomTeachingCycle(existingClassroomId)
        this.loadSubjectsForClassroom(existingClassroomId, existingSubjectId)
      }
    }
    
    // Initialize evaluated category numbering and scale calculation
    this.updateEvaluatedCategoryIndexes()
    this.updateTotalScale()
    
    // Setup form submission handler
    this.setupFormSubmission()
    
    // Initialize field visibility based on current classroom (if any)
    if (classroomSelect && classroomSelect.value && !this.pendingSubjectSelection) {
      this.loadClassroomTeachingCycle(classroomSelect.value)
      this.loadSubjectsForClassroom(classroomSelect.value)
    }
  }

  onClassroomChange(event) {
    console.log("Classroom changed:", event.target.value)
    const classroomId = event.target.value
    
    // Clear any pending subject selection when user manually changes classroom
    this.pendingSubjectSelection = null
    
    if (classroomId) {
      this.loadClassroomTeachingCycle(classroomId)
      this.loadSubjectsForClassroom(classroomId)
    } else {
      this.clearSubjects()
      this.currentTeachingCycle = null
      this.updateFieldVisibility()
    }
  }

  onHasCompetencesChange(event) {
    // Skip for nursery - checkbox is hidden and always true
    if (this.isNurseryValue) return

    console.log("Has competences changed:", event.target.checked)
    const hasCompetences = event.target.checked

    if (hasCompetences) {
      this.showEvaluatedCategoriesSection()
    } else {
      this.hideEvaluatedCategoriesSection()
    }

    this.updateTotalScale()
  }

  onScaleChange(event) {
    console.log("Scale changed:", event.target.value)
    // Only update display if not using evaluated categories
    if (!this.competencesEnabled()) {
      this.updateTotalScale()
    }
  }

  // Legacy method support
  addCompetence(event) {
    this.addEvaluatedCategory(event)
  }

  removeCompetence(event) {
    this.removeEvaluatedCategory(event)
  }

  updateCompetenceScale(event) {
    this.updateEvaluatedCategoryScale(event)
  }

  setupFormSubmission() {
    const form = this.element.querySelector('form')
    if (form) {
      form.addEventListener('submit', (event) => {
        this.cleanupEmptyCategories()
      })
    }
  }

  cleanupEmptyCategories() {
    // Remove or mark empty categories for destruction before form submission
    this.evaluatedCategoryItemTargets.forEach(categoryItem => {
      const nameInput = categoryItem.querySelector('[data-course-form-target="evaluatedCategoryName"]')
      const scaleInput = categoryItem.querySelector('[data-course-form-target="evaluatedCategoryScale"]')
      
      // If name is empty, remove the category item or mark for destruction
      if (nameInput && nameInput.value.trim() === '') {
        const idField = categoryItem.querySelector("input[name*='[id]']")
        
        if (idField && idField.value) {
          // Existing record - mark for destruction
          const destroyField = categoryItem.querySelector("input[name*='[_destroy]']")
          if (destroyField) {
            destroyField.value = "1"
          }
        } else {
          // New record - completely remove the element
          categoryItem.remove()
        }
      }
    })
  }

  showEvaluatedCategoriesSection() {
    if (this.hasEvaluatedCategoriesSectionTarget) {
      this.evaluatedCategoriesSectionTarget.style.display = "block"
    }
  }

  hideEvaluatedCategoriesSection() {
    if (this.hasEvaluatedCategoriesSectionTarget) {
      this.evaluatedCategoriesSectionTarget.style.display = "none"
    }
  }

  addEvaluatedCategory(event) {
    event.preventDefault()

    // Prevent adding categories for nursery (only one description allowed)
    if (this.isNurseryValue) {
      console.log("Cannot add categories for nursery")
      return
    }

    console.log("Adding evaluated category")

    const template = document.getElementById("evaluated-category-template")
    const newCategory = template.cloneNode(true)
    
    // Remove the template ID and show the element
    newCategory.removeAttribute("id")
    newCategory.style.display = "block"
    
    // Update the index in the input names
    const nextIndex = this.evaluatedCategoryItemTargets.length
    const inputs = newCategory.querySelectorAll("input")
    inputs.forEach(input => {
      const name = input.getAttribute("name")
      if (name) {
        input.setAttribute("name", name.replace("INDEX", nextIndex))
      }
      // Set default scale value for new categories (only if not kindergarten)
      if (name && name.includes("[scale]")) {
        input.value = this.isKindergarten() ? "1" : "1"
      }
    })
    
    // Hide scale field for the new category if kindergarten
    const scaleField = newCategory.querySelector('[data-course-form-target="evaluatedCategoryScaleField"]')
    if (scaleField) {
      this.toggleElementVisibility(scaleField, !this.isKindergarten())
    }
    
    // Add to the container
    this.evaluatedCategoriesContainerTarget.appendChild(newCategory)
    
    // Update indexes and total scale
    this.updateEvaluatedCategoryIndexes()
    this.updateTotalScale()
    
    // Focus on the name input
    const nameInput = newCategory.querySelector("[data-course-form-target='evaluatedCategoryName']")
    if (nameInput) {
      nameInput.focus()
    }
  }

  removeEvaluatedCategory(event) {
    event.preventDefault()

    // Prevent removing the description for nursery
    if (this.isNurseryValue) {
      console.log("Cannot remove description for nursery")
      return
    }

    console.log("Removing evaluated category")

    const categoryItem = event.target.closest("[data-course-form-target='evaluatedCategoryItem']")
    if (categoryItem) {
      // Check if this is an existing record by looking for an ID field
      const idField = categoryItem.querySelector("input[name*='[id]']")
      if (idField && idField.value) {
        // This is an existing record, mark for destruction
        const destroyField = categoryItem.querySelector("input[name*='[_destroy]']")
        if (destroyField) {
          destroyField.value = "1"
        }
        categoryItem.style.display = "none"
      } else {
        // This is a new record, just remove it
        categoryItem.remove()
      }
      
      this.updateEvaluatedCategoryIndexes()
      this.updateTotalScale()
    }
  }

  updateEvaluatedCategoryScale(event) {
    // This is called when an evaluated category name or scale changes
    // Update total scale calculation
    console.log("Evaluated category updated:", event.target.value)
    this.updateTotalScale()
  }

  // The competences block is not rendered for secondary schools: no
  // checkbox there means the course is scored on its own scale.
  competencesEnabled() {
    return this.hasHasCompetencesCheckboxTarget && this.hasCompetencesCheckboxTarget.checked
  }

  updateTotalScale() {
    console.log("Updating total scale")

    // Skip scale calculations for kindergarten/nursery
    if (this.isKindergarten() || this.isNurseryValue) {
      if (this.hasTotalScaleDisplayTarget) {
        this.totalScaleDisplayTarget.textContent = "N/A"
      }
      return
    }
    
    let totalScale = 0
    
    if (this.competencesEnabled()) {
      // Calculate total from evaluated categories (only count those with values and names)
      this.evaluatedCategoryScaleTargets.forEach(scaleInput => {
        const categoryItem = scaleInput.closest('[data-course-form-target="evaluatedCategoryItem"]')
        const nameInput = categoryItem.querySelector('[data-course-form-target="evaluatedCategoryName"]')
        
        // Only count if both name and scale have values
        if (nameInput && nameInput.value.trim() !== '' && scaleInput.value !== '') {
          const value = parseInt(scaleInput.value) || 0
          totalScale += value
        }
      })
      
      // Update the scale input to match evaluated categories total
      if (this.hasScaleInputTarget) {
        this.scaleInputTarget.value = totalScale
      }
    } else {
      // Use the manual scale input
      if (this.hasScaleInputTarget) {
        totalScale = parseInt(this.scaleInputTarget.value) || 20
      }
    }
    
    // Update the display
    if (this.hasTotalScaleDisplayTarget) {
      this.totalScaleDisplayTarget.textContent = totalScale
    }
    
    console.log("Total scale updated to:", totalScale)
  }

  updateEvaluatedCategoryIndexes() {
    // Update the name attributes to ensure proper indexing
    this.evaluatedCategoryItemTargets.forEach((item, index) => {
      const inputs = item.querySelectorAll("input")
      inputs.forEach(input => {
        const name = input.getAttribute("name")
        if (name && name.includes("evaluated_categories_attributes")) {
          // Replace any existing index with the current one
          const newName = name.replace(/\[(?:INDEX|\d+)\]/, `[${index}]`)
          input.setAttribute("name", newName)
        }
      })
    })
  }

  loadClassroomTeachingCycle(classroomId) {
    console.log("Loading teaching cycle for classroom:", classroomId)
    
    fetch(`/schools/books/courses/classroom_teaching_cycle?classroom_id=${classroomId}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
      .then(response => {
        console.log("Teaching cycle response status:", response.status)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON')
        }
        return response.json()
      })
      .then(data => {
        console.log("Teaching cycle data received:", data)
        this.currentTeachingCycle = data.teaching_cycle_code
        this.updateFieldVisibility()
      })
      .catch(error => {
        console.error('Error loading teaching cycle for classroom:', error)
        this.currentTeachingCycle = null
        this.updateFieldVisibility()
      })
  }

  loadSubjectsForClassroom(classroomId, existingSubjectId = null) {
    // Once saved, the subject is locked: a hidden field (same id) replaces
    // the select, and there is nothing to reload.
    if (!(document.getElementById("course_subject_id") instanceof HTMLSelectElement)) return

    console.log("Loading subjects for classroom:", classroomId)
    
    // Use the endpoint that gets subjects directly by classroom ID
    fetch(`/schools/books/courses/subjects_for_classroom?classroom_id=${classroomId}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
      .then(response => {
        console.log("Subjects response status:", response.status)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON')
        }
        return response.json()
      })
      .then(subjects => {
        console.log("Subjects received:", subjects)
        this.updateSubjectOptions(subjects, existingSubjectId)
      })
      .catch(error => {
        console.error('Error loading subjects for classroom:', error)
        this.clearSubjects()
      })
  }

  updateSubjectOptions(subjects, existingSubjectId = null) {
    const subjectSelect = document.getElementById("course_subject_id")
    if (!subjectSelect) {
      console.error("Subject select element not found!")
      return
    }
    
    // The server's own (translated) prompt, kept across reloads.
    const prompt = subjectSelect.querySelector('option[value=""]')?.textContent || ""
    subjectSelect.innerHTML = ""

    // Add default option
    const defaultOption = document.createElement("option")
    defaultOption.value = ""
    defaultOption.textContent = prompt
    subjectSelect.appendChild(defaultOption)
    
    // Use pending selection if available, otherwise use passed existingSubjectId
    const subjectIdToSelect = this.pendingSubjectSelection || existingSubjectId
    
    // Add subject options
    subjects.forEach(subject => {
      const option = document.createElement("option")
      option.value = subject.id
      option.textContent = subject.name
      if (subjectIdToSelect && subject.id.toString() === subjectIdToSelect.toString()) {
        option.selected = true
        console.log("Selected subject:", subject.name)
      }
      subjectSelect.appendChild(option)
    })
    
    // Clear pending selection after use
    if (this.pendingSubjectSelection) {
      this.pendingSubjectSelection = null
    }
    
    console.log("Subject options updated, total options:", subjectSelect.options.length)
  }

  clearSubjects() {
    const subjectSelect = document.getElementById("course_subject_id")
    if (!subjectSelect) {
      console.error("Subject select element not found!")
      return
    }
    
    const prompt = subjectSelect.querySelector('option[value=""]')?.textContent || ""
    subjectSelect.innerHTML = ""
    const defaultOption = document.createElement("option")
    defaultOption.value = ""
    defaultOption.textContent = prompt
    subjectSelect.appendChild(defaultOption)
  }

  updateFieldVisibility() {
    console.log("Updating field visibility for teaching cycle:", this.currentTeachingCycle)

    const isSecondary = this.currentTeachingCycle === 'SECONDARY'
    const isKindergarten = this.currentTeachingCycle === 'NURSERY'

    console.log("Is secondary:", isSecondary)
    console.log("Is kindergarten:", isKindergarten)

    // Hide/show coefficient field - only for secondary
    this.toggleFieldVisibility("coefficientField", isSecondary)

    // Hide/show scale field - for secondary and primary (not nursery)
    this.toggleFieldVisibility("scaleField", !isKindergarten)

    // Hide/show scale summary - for secondary and primary (not nursery)
    this.toggleFieldVisibility("scaleSummary", !isKindergarten)

    // Hide/show evaluated category scale fields - for secondary and primary (not nursery)
    this.evaluatedCategoryScaleFieldTargets.forEach(field => {
      this.toggleElementVisibility(field, !isKindergarten)
    })

    // If kindergarten, set default values and disable scale calculations
    if (isKindergarten) {
      this.setKindergartenDefaults()
    } else if (!isSecondary) {
      // For PRIMARY: set default coefficient to 1 (hidden but needed for database)
      this.setPrimaryDefaults()
    } else {
      this.updateTotalScale()
    }
  }

  setPrimaryDefaults() {
    // Set default coefficient to 1 for primary (not visible but still saved)
    if (this.hasCoefficientFieldTarget) {
      const coefficientInput = this.coefficientFieldTarget.querySelector('input[type="number"]')
      if (coefficientInput) {
        coefficientInput.value = 1
      }
    }
    this.updateTotalScale()
  }

  toggleFieldVisibility(targetName, show) {
    const targets = this[`${targetName}Targets`]
    if (targets && targets.length > 0) {
      targets.forEach(target => this.toggleElementVisibility(target, show))
    }
  }

  toggleElementVisibility(element, show) {
    if (show) {
      element.style.display = ""
      element.style.visibility = "visible"
    } else {
      element.style.display = "none"
      element.style.visibility = "hidden"
    }
  }

  setKindergartenDefaults() {
    // Set default coefficient to 1 for kindergarten
    if (this.hasCoefficientFieldTarget) {
      const coefficientInput = this.coefficientFieldTarget.querySelector('input[type="number"]')
      if (coefficientInput) {
        coefficientInput.value = 1
      }
    }

    // Set default scale to 1 for kindergarten (not used in calculations)
    if (this.hasScaleInputTarget) {
      this.scaleInputTarget.value = 1
    }

    // Set all evaluated category scales to 1 for kindergarten
    this.evaluatedCategoryScaleTargets.forEach(scaleInput => {
      scaleInput.value = 1
    })

    // Update total scale display to show competency-based evaluation
    if (this.hasTotalScaleDisplayTarget) {
      this.totalScaleDisplayTarget.textContent = "N/A"
    }

    console.log("Set kindergarten defaults")
  }

  isKindergarten() {
    return this.currentTeachingCycle === 'NURSERY'
  }
} 